<template>
  <!-- scale は他フォーマット同様ルート要素に当てる。EprintDialog は $el.innerHTML で抽出するため、
       内側 div に scale を置くと transform が PDF 送信 HTML に漏れて縮小・崩れの原因になる -->
  <div
    :style="atenaScaleStyle"
    :class="{ 'is-sample': isSample, 'is-preview': !isSample }"
  >
    <!-- PDF用のページ設定。$el.innerHTML で抽出され Puppeteer(preferCSSPageSize) が解釈する。
         @page で用紙向き・サイズを確定（L版は横長）し margin:0 で印刷可能領域いっぱいに描画＝右切れ防止。
         @media print で各シートを1ページに収め、余分な空白ページ（eprintの課金ページ増）を防ぐ。
         <style> はテンプレート直書きだと Vue2 が除去するため :is='style' で描画する -->
    <component :is="'style'">{{ pdfPageCss }}</component>
    <!-- 1枚目: L判=上段に宛先2面+下段に差出人2面(シール紙1枚運用) / 縦(A4)=宛先のみ10面 -->
    <div :id="idPrefix + 'preview-page-01'" :style="sheetStyle">
      <div
        v-for="(cell, i) in page1Cells"
        :key="i"
        :style="cellStyle"
        @click="cell.type === 'atena' && navigateToAtenaInfo()"
      >
        <template v-if="cell.type === 'atena' && cell.item">
          <div
            style="display: flex; justify-content: flex-end; margin-bottom: 2mm"
          >
            <span :style="postalStyle"
              >&#12306; {{ formatPostNumber(cell.item.postNumber) }}</span
            >
          </div>
          <div
            :style="
              fieldStyle(
                cell.item.address,
                'address',
                'font-size:' +
                  fz.body +
                  '; line-height:1.5; margin-bottom:1mm; white-space:pre-wrap; word-break:break-all;'
              )
            "
          >
            {{ cell.item.address }}
          </div>
          <div
            :style="
              fieldStyle(
                cell.item.companyName,
                'companyName',
                'font-size:' + fz.body + '; line-height:1.4; margin-bottom:1mm;'
              )
            "
          >
            {{ cell.item.companyName }}
          </div>
          <div style="padding-top: 1mm; text-align: right">
            <div
              :style="
                fieldStyle(
                  cell.item.departmentName,
                  'departmentName',
                  'font-size:' + fz.body + ';'
                )
              "
            >
              {{ cell.item.departmentName }}
            </div>
            <div
              :style="
                fieldStyle(
                  recipientName(cell.item),
                  'staffName',
                  'font-size:' + fz.name + '; letter-spacing:0.15em;'
                )
              "
            >
              {{ recipientName(cell.item)
              }}<span :style="'font-size:' + fz.honor + '; margin-left:1mm;'">{{
                honorific(cell.item)
              }}</span>
            </div>
          </div>
        </template>
        <template v-else-if="cell.type === 'sender' && hasSenderData">
          <div
            style="display: flex; justify-content: flex-end; margin-bottom: 2mm"
          >
            <span :style="postalStyle"
              >&#12306; {{ formatPostNumber(formatData.postNumber) }}</span
            >
          </div>
          <div
            :style="
              fieldStyle(
                senderAddress,
                'address',
                'font-size:' +
                  fz.body +
                  '; line-height:1.5; margin-bottom:1mm; white-space:pre-wrap; word-break:break-all;'
              )
            "
          >
            {{ senderAddress }}
          </div>
          <div style="margin-top: auto; padding-top: 1mm; text-align: right">
            <span
              :style="
                fieldStyle(
                  senderName,
                  'staffName',
                  'font-size:' + fz.name + '; letter-spacing:0.15em;'
                )
              "
              >{{ senderName }}</span
            >
          </div>
        </template>
      </div>
    </div>

    <!-- 2枚目: 差出人ラベル（縦のみ。L判は1枚目に差出人を同居させるため出さない） -->
    <div
      v-if="!isLSize"
      :id="idPrefix + 'preview-page-02'"
      :style="senderSheetStyle"
    >
      <div v-for="i in 10" :key="'sender-' + i" :style="cellStyle">
        <template v-if="hasSenderData">
          <div
            style="display: flex; justify-content: flex-end; margin-bottom: 2mm"
          >
            <span :style="postalStyle"
              >&#12306; {{ formatPostNumber(formatData.postNumber) }}</span
            >
          </div>
          <div
            :style="
              fieldStyle(
                senderAddress,
                'address',
                'font-size:' +
                  fz.body +
                  '; line-height:1.5; margin-bottom:1mm; white-space:pre-wrap; word-break:break-all;'
              )
            "
          >
            {{ senderAddress }}
          </div>
          <div style="margin-top: auto; padding-top: 1mm; text-align: right">
            <span
              :style="
                fieldStyle(
                  senderName,
                  'staffName',
                  'font-size:' + fz.name + '; letter-spacing:0.15em;'
                )
              "
              >{{ senderName }}</span
            >
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { emitter } from '@/plugins/emitter'
import {
  ATENA_CHAR_LIMITS,
  isAtenaLSize,
  type AtenaItem
} from '@/utils/format-edit/atenaFormat'

const FONT_FAMILY =
  "font-family:'Noto Sans JP','Noto Sans Japanese',sans-serif;"

type AtenaPreviewProps = {
  formatData: Record<string, any>
  basicData?: Record<string, any>
  previewScale?: number
  currentCardId?: string | string[]
  idPrefix?: string
  isSample?: boolean
}

const props = withDefaults(defineProps<AtenaPreviewProps>(), {
  basicData: () => ({}),
  previewScale: 1,
  currentCardId: '',
  idPrefix: '',
  isSample: false
})

type Page1Cell = { type: 'atena' | 'sender'; item?: AtenaItem }

const atenaItems = computed<AtenaItem[]>(
  () => (props.formatData && props.formatData.atenaItems) || []
)

const isLSize = computed(() =>
  isAtenaLSize(props.formatData && props.formatData.selectedPaper)
)

// PDF出力用の @page / print スタイル。用紙向きに合わせてページサイズを確定する
// L版＝コンビニ写真プリントの L判(127×89mm 横)。証明写真(Idphotoprint)と同じ寸法
const pdfPageCss = computed(() => {
  const w = isLSize.value ? '127mm' : '210mm'
  const h = isLSize.value ? '89mm' : '297mm'
  const p1 = '#' + props.idPrefix + 'preview-page-01'
  const p2 = '#' + props.idPrefix + 'preview-page-02'
  // padding はリセットしない＝シートの padding が外周余白として印刷にも残る。
  // break-after はシートが2枚ある縦のみ（L判は1枚。末尾に break を置くと空白ページが生まれる恐れ）
  return (
    '@page{size:' +
    w +
    ' ' +
    h +
    ';margin:0;}' +
    '@media print{' +
    p1 +
    ',' +
    p2 +
    '{margin:0!important;min-height:0!important;}' +
    (isLSize.value ? '' : p1 + '{break-after:page;}') +
    '}'
  )
})

// 1枚目のセル構成。L判はシール紙1枚しか出力できない想定なので 宛先2+差出人2 を1枚に同居させる
const page1Cells = computed<Page1Cell[]>(() => {
  if (isLSize.value) {
    return [
      { type: 'atena', item: atenaItems.value[0] },
      { type: 'atena', item: atenaItems.value[1] },
      { type: 'sender' },
      { type: 'sender' }
    ]
  }
  const cells: Page1Cell[] = []
  for (let i = 0; i < 10; i++) {
    cells.push({ type: 'atena', item: atenaItems.value[i] })
  }
  return cells
})

const senderName = computed(() => {
  const data = props.formatData || {}
  return (data.lastName || '') + (data.firstName || '')
})

const senderAddress = computed(() => {
  const data = props.formatData || {}
  return [data.address1, data.address2].filter(Boolean).join('\n')
})

const hasSenderData = computed(() => {
  const data = props.formatData || {}
  return !!(data.postNumber || senderAddress.value || senderName.value)
})

const charLimits = computed(() =>
  isLSize.value ? ATENA_CHAR_LIMITS.lsize : ATENA_CHAR_LIMITS.portrait
)

// previewScale は A4幅(794px)基準。L判は実シート幅(約480px)基準に換算し直して
// 「収まるなら等倍、狭ければフィット」させる。ただし上限1（拡大不可）。
// 上限を外すと全幅 root が拡大されビューポートを超え、横スクロール＝中央ずれになる。
const atenaScaleStyle = computed(() => {
  const A4_WIDTH_PX = 794
  const L_WIDTH_PX = 480 // 127mm @96dpi
  const scale = isLSize.value
    ? Math.min(1, props.previewScale * (A4_WIDTH_PX / L_WIDTH_PX))
    : props.previewScale
  return {
    transform: `scale(${scale})`,
    'transform-origin': 'top center'
  }
})

const sheetStyle = computed(() => {
  const w = isLSize.value ? '127mm' : '210mm'
  const h = isLSize.value ? '89mm' : '297mm'
  const pad = isLSize.value ? '3mm' : '5mm'
  return `width:${w}; min-height:${h}; background:#fff; box-sizing:border-box; display:grid; grid-template-columns:1fr 1fr; align-content:start; padding:${pad}; margin:2rem auto 0;`
})

const senderSheetStyle = computed(() =>
  sheetStyle.value.replace('margin:2rem auto 0;', 'margin:10mm auto 0;')
)

const cellStyle = computed(() => {
  // シートの padding(外周余白)を含めて1ページに収まる高さ。
  // L判: 3mm*2 + 41.3mm*2 = 88.6mm < 89mm / 縦: 5mm*2 + 57.3mm*5 = 296.5mm < 297mm
  // （丁度埋めにすると mm→px 丸めで次ページに溢れるため 0.4〜0.5mm のゆとりを持たせる）
  const h = isLSize.value ? '41.3mm' : '57.3mm'
  const pad = isLSize.value ? '2mm 3mm' : '4mm 5mm'
  return (
    'border:0.5px solid #ccc; padding:' +
    pad +
    '; box-sizing:border-box; display:flex; flex-direction:column; justify-content:center; overflow:visible; cursor:pointer; height:' +
    h +
    ';'
  )
})

// 用紙ごとのフォントサイズ。L判はセルが小さい(約60×41mm)ため縮小
const fz = computed(() =>
  isLSize.value
    ? { post: '7pt', body: '7pt', name: '11pt', honor: '9pt' }
    : { post: '12pt', body: '12pt', name: '18pt', honor: '14pt' }
)

const postalStyle = computed(
  () => 'font-size:' + fz.value.post + '; ' + FONT_FAMILY
)

function isOver(
  text: string | undefined,
  field: keyof typeof charLimits.value
) {
  if (!text) return false
  return text.length > charLimits.value[field]
}

// ベーススタイルに font-family を付与し、超過時は color:red を追加
function fieldStyle(
  text: string | undefined,
  field: keyof typeof charLimits.value,
  base: string
) {
  const over = isOver(text, field)
  return `${base} ${FONT_FAMILY}${over ? ' color:red;' : ''}`
}

function navigateToAtenaInfo() {
  if (props.isSample) return
  emitter.emit('go-card-to', 'atena-info')
}

function formatPostNumber(val: string | undefined) {
  if (!val) return ''
  const n = String(val)
    .replace(/-/g, '')
    .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
  if (n.length >= 7) return n.slice(0, 3) + '-' + n.slice(3, 7)
  return n
}

function recipientName(item: AtenaItem) {
  if (item.staffName) return item.staffName
  if (item.companyName) return item.companyName
  return ''
}

function honorific(item: AtenaItem) {
  if (item.staffName) return '様'
  if (item.companyName || item.departmentName) return '御中'
  return ''
}
</script>
